<template>
    <section v-if="visible" class="flex flex-col gap-3">
        <h2 class="body-small-semibold text-valence-stone-900">Suggested Goals</h2>

        <!-- Loading state -->
        <div v-if="loading" class="flex items-center gap-3 py-6 justify-center">
            <LoadingSpinner class="w-5 h-5 text-valence-stone-500" />
            <span class="body-small text-valence-stone-500">Generating goal suggestions…</span>
        </div>

        <!-- Error state -->
        <BaseNotification v-else-if="error" theme="error" :text="error" hide-dismiss />

        <!-- Goals list -->
        <div v-else-if="visibleGoals.length > 0" class="flex flex-col gap-3">
            <BaseCard v-for="goal in visibleGoals" :key="goal.id" padding="lg" class="flex flex-col gap-2.5">
                <div class="flex flex-col gap-0.5">
                    <div class="flex items-start gap-2.5">
                        <!-- Hourglass "journey/goal" glyph — teal, distinct from the
                             self-reflection amber so the goal cards read as a related
                             but separate object type. Colors + path match the mock. -->
                        <svg class="goal-icon mt-1 shrink-0" viewBox="0 0 12.6317 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path
                                d="M12.6316 15.1557C12.5958 13.1841 11.9474 11.5628 10.6863 10.2895C9.46316 9.05673 8.00842 8.43246 6.32211 8.42123C2.82737 8.39429 0.00210526 11.4438 0 15.1579H12.6316V15.1557Z"
                                fill="#30A29D" />
                            <path
                                d="M0 1.26319C0.0357895 3.3587 0.684212 5.08189 1.94526 6.43514C3.16842 7.74543 4.62316 8.40893 6.30947 8.42086C9.80421 8.45189 12.6295 5.21077 12.6316 1.26319H0Z"
                                fill="#81DADA" />
                            <rect y="14.7368" width="12.6316" height="1.26316" fill="#30A29D" />
                            <rect x="0.000115795" width="12.6316" height="1.26316" fill="#81DADA" />
                        </svg>
                        <span class="body-semibold text-valence-stone-900">{{ goal.title }}</span>
                    </div>
                    <p v-if="goal.description" class="body-small text-valence-stone-500 pl-6">{{ goal.description }}</p>
                </div>
                <div class="flex items-center gap-3 flex-wrap pl-6">
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        class="base-btn-square"
                        :disabled="pendingId === goal.id || goal.prioritized_at !== null"
                        @click="onTogglePrioritize(goal)">
                        <template v-if="goal.prioritized_at"> <i class="bi bi-check-lg" aria-hidden="true"></i> Added </template>
                        <template v-else> Add goal </template>
                    </BaseButton>
                    <button
                        type="button"
                        class="body-small text-valence-stone-500 hover:text-valence-stone-900 disabled:opacity-40"
                        :disabled="pendingId === goal.id"
                        @click="onDismiss(goal)">
                        {{ pendingId === goal.id ? "…" : "Dismiss" }}
                    </button>
                </div>
            </BaseCard>

            <!-- Inline warning for prioritize failures (3-goal cap or request error) -->
            <BaseNotification v-if="priorityCapError" theme="error" hide-dismiss>
                <template #content>
                    <p class="body-small">
                        <i class="bi bi-exclamation-triangle mr-1.5" aria-hidden="true"></i>{{ priorityCapError }}
                        <a v-if="priorityCapExceeded" href="/journeys" class="underline">Manage your goals in Journeys</a>
                    </p>
                </template>
            </BaseNotification>

            <div class="flex items-center justify-end pt-1">
                <a href="/journeys" class="body-small text-valence-stone-500 hover:text-valence-stone-900 flex items-center gap-1" @click="onViewAllGoals">
                    View all goals in Journeys <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </a>
            </div>
        </div>

        <p v-else class="body-small text-valence-stone-500 py-2">Could not generate suggested goals.</p>
    </section>
</template>

<script setup lang="ts">
import { fetchApi, useFetch } from "~vue/api";
import type { SuggestedGoalResponse, SuggestGoalsResponse } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";

type SuggestedGoal = SuggestedGoalResponse;

const props = defineProps<{
    cycleId: string;
}>();

const emit = defineEmits<{
    // Fired when the section hides itself (journeys flag off) so the parent can reclaim the rail's space.
    hidden: [];
}>();

const MAX_PRIORITIZED_GOALS = 3;

const visible = ref(true);
const loading = ref(true);
const error = ref<string | null>(null);
const goals = ref<SuggestedGoal[]>([]);
const dismissedIds = ref<Set<string>>(new Set());
const pendingId = ref<string | null>(null);
const priorityCapError = ref<string | null>(null);
const priorityCapExceeded = ref(false);

// Dismiss only hides locally after a successful DELETE — no need for a
// separate "dismissed" state since the archived goal disappears from
// /suggest-goals results on subsequent loads.
const visibleGoals = computed(() => goals.value.filter((g) => !dismissedIds.value.has(g.id)));

async function fetchGoals() {
    loading.value = true;
    error.value = null;
    const { data, error: fetchError, statusCode } = await useFetch(`/v1/reflections/cycles/${props.cycleId}/suggest-goals`).post().json<SuggestGoalsResponse>();
    loading.value = false;
    if (fetchError.value || !data.value) {
        // If journeys flag is off, hide section entirely
        if (statusCode.value === 403) {
            visible.value = false;
            emit("hidden");
            return;
        }
        // No status at all means the request never completed — canceled by the
        // fetch layer (auth token fetch failed) or a network failure. Both hit the
        // try/catch before this was on useFetch; keep that path's copy.
        if (statusCode.value == null) {
            error.value = "Could not generate goal suggestions. Please try again.";
            return;
        }
        error.value = fetchError.value?.message || "Could not generate goal suggestions.";
        return;
    }
    goals.value = data.value.goals ?? [];
}

async function onTogglePrioritize(goal: SuggestedGoal) {
    priorityCapError.value = null;
    priorityCapExceeded.value = false;
    const isPrioritized = goal.prioritized_at !== null;

    pendingId.value = goal.id;
    try {
        // Prioritizing also confirms the goal (source: assessment -> confirmed) so it
        // appears as a regular prioritized goal on the Journeys board — same pattern
        // as adopting an extracted goal there.
        const response = await fetchApi(`/v1/goals/${goal.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(isPrioritized ? { is_prioritized: false } : { is_prioritized: true, confirm_goal: true }),
        });
        if (response.ok) {
            const updated = (await response.json()) as SuggestedGoal;
            const idx = goals.value.findIndex((g) => g.id === goal.id);
            if (idx !== -1) {
                goals.value[idx] = { ...goals.value[idx], prioritized_at: updated.prioritized_at };
            }
            if (!isPrioritized) {
                void logUserInteraction(AmplitudeEventNames.GOAL_PRIORITIZED, {
                    cycle_id: props.cycleId,
                    goal_id: goal.id,
                    page: "assessment_results",
                    method: "button",
                    new_state: "prioritized",
                });
            }
        } else if (response.status === 409) {
            priorityCapExceeded.value = true;
            priorityCapError.value = `You can add up to ${MAX_PRIORITIZED_GOALS} goals. Remove one to make room.`;
        } else {
            priorityCapError.value = "Could not update the goal. Please try again.";
        }
    } finally {
        pendingId.value = null;
    }
}

async function onDismiss(goal: SuggestedGoal) {
    pendingId.value = goal.id;
    try {
        const response = await fetchApi(`/v1/goals/${goal.id}`, { method: "DELETE" });
        if (response.ok || response.status === 404) {
            // 404 means someone else already deleted it — treat as success.
            dismissedIds.value.add(goal.id);
        }
    } finally {
        pendingId.value = null;
    }
}

function onViewAllGoals() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_VIEW_ALL_GOALS_CLICKED, {
        cycle_id: props.cycleId,
    });
}

onMounted(() => {
    void fetchGoals();
});
</script>

<style scoped>
.goal-icon {
    width: 12.63px;
    height: 16px;
}
</style>
